/**
 * Centralised app-wide constants.
 * Update these values here — do NOT hardcode them elsewhere.
 */

export const APP_VERSION = '1.15.1';

/**
 * F46 (Grant directive 2026-07-13): Research Brief is NOT part of the product.
 * Server flag RESEARCH_BRIEF_ENABLED is "off"; this constant hides every UI
 * entry point (sidebar item, auto-suggest banner, checklist nudge). Flip both
 * together if it's ever deliberately resurrected.
 */
export const RESEARCH_BRIEF_UI_ENABLED = false;

/**
 * F50 (Grant directive 2026-07-13): Compliance Hub only exposes surfaces that
 * have passed Phase D verification. Paused surfaces stay visible as
 * coming-soon placeholders and are re-enabled per-flag when their
 * verification batch passes:
 *  - Jurisdictions comparison → returns after batch D-2
 *  - Image (photo) analysis   → returns after batch D-4
 * Tool pages (Report Builder, Copy Builder, Manual Review, Compliance Files)
 * are gated via coming-soon routes in Sidebar.tsx.
 */
export const JURISDICTIONS_UI_ENABLED = false;
export const IMAGE_ATTACH_UI_ENABLED = false;

/**
 * Corpus stats — the SINGLE source of truth for corpus counts across the app.
 *
 * The values below are the build-time FALLBACK, shown before the API responds
 * or if the live endpoint fails. The live source of truth is the public
 * `GET /api/corpus-stats` endpoint, which is fetched once on app boot and
 * mutated into this object via `hydrateCorpusStats()` (see App boot).
 *
 * A6 (2026-06-20): centralised so a corpus refresh requires no frontend change —
 * the live counts flow in automatically. Update the fallback numbers below only
 * when you want the pre-hydration display to match a verified corpus refresh.
 *
 * Note: this is a mutable module singleton (not `as const`) so the boot-time
 * hydrate can patch it. Components read it after mount, so they see live values.
 */
export const CORPUS_STATS = {
  domains:    14,
  clauses:    5696,   // 5626 + 70 DDA Premises Standards 2010 (uploaded 2026-06-20)
  crossRefs:  14933,
  pdfs:       71,     // +1 Premises Standards 2010
  pageImages: 3299,
};

/**
 * Patch CORPUS_STATS in place from a live `/api/corpus-stats` payload.
 * Only overwrites numeric fields that are present (crossRefs is omitted by the
 * endpoint since it isn't stored in D1, so the fallback value is preserved).
 */
export function hydrateCorpusStats(live: Partial<typeof CORPUS_STATS> | null | undefined): void {
  if (!live) return;
  for (const key of Object.keys(CORPUS_STATS) as (keyof typeof CORPUS_STATS)[]) {
    const v = live[key];
    if (typeof v === 'number' && v > 0) {
      CORPUS_STATS[key] = v;
    }
  }
}
